import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaBell,
  FaSyncAlt,
  FaCalendarAlt,
  FaChartLine,
  FaChevronDown,
  FaCheckCircle,
  FaCogs,
  FaExclamationTriangle,
  FaGlobeAmericas,
  FaRegHandPaper,
  FaStopwatch,
  FaTasks,
  FaTimesCircle,
  FaUser,
  FaUsers,
  FaWpforms,
} from "react-icons/fa";
import api from "../api";
import { useAuth } from "../AuthContext";
import ProcessRunner from "./ProcessRunner";
import Agenda from "./Agenda";
import { getAssistantReply } from "../utils/assistantKnowledge";
import { triggerAssistantAction } from "../utils/assistantActions";
import { extractResponsibleTargets } from "../utils/responsibles";
import {
  buildIndicatorDataset,
  buildIndicatorProcessOptions,
  buildStatusDonutBackground,
  fetchAllCompletedIndicators,
  INDICATOR_COMPLETED_PAGE_SIZE,
} from "./homeDashboardIndicators";

const BLUE_BACKGROUNDS = [
  "from-blue-50 to-blue-100",
  "from-sky-50 to-blue-100",
  "from-blue-100 to-blue-200",
  "from-sky-100 to-blue-200",
];

const METRIC_CARD_THEMES = {
  alert: {
    background: "from-amber-50 to-amber-100",
    textColor: "text-amber-900",
    labelColor: "text-amber-600",
    descriptionColor: "text-amber-700",
    iconWrapper: "bg-amber-100 text-amber-600",
    focusRing: "focus-visible:ring-amber-500",
    activeRing: "ring-amber-400",
    inactiveRing: "ring-amber-100",
  },
  late: {
    background: "from-red-50 to-red-100",
    textColor: "text-red-900",
    labelColor: "text-red-600",
    descriptionColor: "text-red-700",
    iconWrapper: "bg-red-100 text-red-600",
    focusRing: "focus-visible:ring-red-500",
    activeRing: "ring-red-400",
    inactiveRing: "ring-red-100",
  },
};

const getMetricCardTheme = (cardId, index) => {
  if (METRIC_CARD_THEMES[cardId]) {
    return METRIC_CARD_THEMES[cardId];
  }

  return {
    background: BLUE_BACKGROUNDS[index % BLUE_BACKGROUNDS.length],
    textColor: "text-blue-900",
    labelColor: "text-blue-500",
    descriptionColor: "text-blue-600",
    iconWrapper: "bg-blue-100 text-blue-600",
    focusRing: "focus-visible:ring-blue-500",
    activeRing: "ring-blue-400",
    inactiveRing: "ring-blue-100",
  };
};

const formatDuration = (milliseconds) => {
  if (!milliseconds || Number.isNaN(milliseconds)) return "-";
  const totalHours = Math.max(milliseconds / (1000 * 60 * 60), 0);
  if (totalHours >= 48) {
    return `${Math.round(totalHours / 24)}d`;
  }
  if (totalHours >= 1) {
    return `${Math.round(totalHours)}h`;
  }
  return "<1h";
};

const formatNumber = (value) => {
  if (value == null) return "-";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return numeric.toLocaleString("pt-BR");
};

const COMPLETED_ANALYTICS_DAYS = 365;
const COMPLETED_ANALYTICS_PAGE_SIZE = INDICATOR_COMPLETED_PAGE_SIZE;

const normalizeId = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return null;
};

const toNormalizedArray = (values) => {
  if (!Array.isArray(values)) return [];
  const unique = new Set();
  values.forEach((value) => {
    const normalized = normalizeId(value);
    if (normalized) unique.add(normalized);
  });
  return Array.from(unique);
};

const parseInstanceDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const inferStepStartDate = (instance) => {
  if (!instance) return null;
  const candidateKeys = [
    "currentStepStartedAt",
    "currentStepStartAt",
    "currentStepStartDate",
    "currentStepAssignedAt",
    "currentStepEnteredAt",
    "currentStepArrivalDate",
    "startedAt",
  ];

  for (const key of candidateKeys) {
    const parsed = parseInstanceDate(instance[key]);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

const getInstanceKey = (instance) => {
  if (!instance) return null;
  if (instance.id !== undefined && instance.id !== null) {
    return String(instance.id);
  }
  if (instance.activityId !== undefined && instance.activityId !== null) {
    return String(instance.activityId);
  }
  if (instance.processInstanceId !== undefined && instance.processInstanceId !== null) {
    return String(instance.processInstanceId);
  }
  return null;
};

const NO_DEADLINE_LABEL = "Sem prazo";

const ALERT_WINDOW_MS = 24 * 60 * 60 * 1000;
const TASKS_PER_PROCESS_PAGE = 10;
const TASK_SUMMARY_INLINE_LIMIT = 2;

const AI_GREETING_TEMPLATES = [
  (name) => `${name}, posso ajudar agora?`,
  (name) => `${name}, vamos conversar?`,
  (name) => `${name}, precisa de ajuda?`,
  (name) => `${name}, me conte sua necessidade.`,
  (name) => `${name}, pergunte ou peça algo.`,
];

const buildDashboardAiGreeting = (firstName, seed = Date.now()) => {
  const safeName = firstName || "Olá";
  const index = Math.abs(Math.floor(seed)) % AI_GREETING_TEMPLATES.length;
  return AI_GREETING_TEMPLATES[index](safeName);
};

const getDeadlineStatus = (instance) => {
  if (!instance.currentStepDeadline) {
    return { label: NO_DEADLINE_LABEL, color: "text-sky-600" };
  }
  const deadline = new Date(instance.currentStepDeadline);
  const diff = deadline.getTime() - Date.now();
  if (diff < 0) {
    return { label: "Prazo excedido", color: "text-rose-600" };
  }
  if (diff < ALERT_WINDOW_MS) {
    return { label: "Menos de 24h", color: "text-amber-600" };
  }
  const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
  return { label: `Vence em ${days}d`, color: "text-emerald-600" };
};

export default function HomeDashboard({ openTab, refreshKey }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksLoadProgress, setTasksLoadProgress] = useState(0);
  const [loadingGroups, setLoadingGroups] = useState(() => new Set());
  const preloadedProcessGroupsRef = useRef(new Set());
  const [, setCounts] = useState({ total: 0, overdue: 0, open: 0, completed: 0 });
  const [activePanel, setActivePanel] = useState("overview");
  const [panelRefreshToken, setPanelRefreshToken] = useState(0);
  const [globalOpenItems, setGlobalOpenItems] = useState([]);
  const [globalCompletedItems, setGlobalCompletedItems] = useState([]);
  const [globalProcesses, setGlobalProcesses] = useState([]);
  const [globalForms, setGlobalForms] = useState([]);
  const [recentDays, setRecentDays] = useState(7);
  const [indicatorPeriodDays, setIndicatorPeriodDays] = useState(90);
  const [indicatorProcess, setIndicatorProcess] = useState("all");
  const [indicatorStatus, setIndicatorStatus] = useState("all");
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [advancedError, setAdvancedError] = useState("");
  const [advancedWarnings, setAdvancedWarnings] = useState([]);
  const [advancedUpdatedAt, setAdvancedUpdatedAt] = useState(null);
  const [completedHistoryCount, setCompletedHistoryCount] = useState(0);
  const [completedHistoryComplete, setCompletedHistoryComplete] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [todayMessages, setTodayMessages] = useState([]);
  const [taskFilter, setTaskFilter] = useState("all");
  const [expandedGroupKeys, setExpandedGroupKeys] = useState(() => new Set());
  const [visibleTasksByGroup, setVisibleTasksByGroup] = useState({});
  const [isAiPromptActive, setIsAiPromptActive] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiPromptPending, setAiPromptPending] = useState(false);

  const extractResponsible = useCallback(extractResponsibleTargets, []);

  const evaluateTaskDeadline = useCallback((instance) => {
    const deadline = parseInstanceDate(instance?.currentStepDeadline);
    if (!deadline) {
      return { status: "onTime", remaining: Number.POSITIVE_INFINITY };
    }

    const deadlineTime = deadline.getTime();
    if (!Number.isFinite(deadlineTime)) {
      return { status: "onTime", remaining: Number.POSITIVE_INFINITY };
    }

    const now = Date.now();
    const remaining = deadlineTime - now;
    if (remaining < 0) {
      return { status: "late", remaining };
    }

    const startedAt = inferStepStartDate(instance);
    if (startedAt) {
      const totalDuration = deadlineTime - startedAt.getTime();
      if (totalDuration > 0) {
        const alertThreshold = totalDuration * 0.2;
        if (remaining <= alertThreshold) {
          return { status: "alert", remaining };
        }
      }
    }

    if (remaining <= ALERT_WINDOW_MS) {
      return { status: "alert", remaining };
    }

    return { status: "onTime", remaining };
  }, []);

  const refreshPanelData = useCallback(() => {
    setPanelRefreshToken((prev) => prev + 1);
  }, []);

  const handlePanelSelect = useCallback(
    (panelId) => {
      setActivePanel((current) => {
        refreshPanelData();
        if (current === panelId) {
          return current;
        }
        return panelId;
      });
    },
    [refreshPanelData]
  );

  const showMoreGroupTasks = useCallback((groupKey, totalItems) => {
    if (!groupKey || !totalItems) {
      return;
    }

    setVisibleTasksByGroup((prev) => {
      const currentVisible = prev[groupKey] || TASKS_PER_PROCESS_PAGE;
      const nextVisible = Math.min(currentVisible + TASKS_PER_PROCESS_PAGE, totalItems);

      if (nextVisible === currentVisible) {
        return prev;
      }

      return {
        ...prev,
        [groupKey]: nextVisible,
      };
    });
  }, []);

  const setGroupLoading = useCallback((groupKey, isLoading) => {
    if (!groupKey) return;
    setLoadingGroups((prev) => {
      const next = new Set(prev);
      if (isLoading) {
        next.add(groupKey);
      } else {
        next.delete(groupKey);
      }
      return next;
    });
  }, []);

  const loadProcessTasks = useCallback(async (groupKey) => {
    if (!groupKey || loadingGroups.has(groupKey)) {
      return;
    }

    setGroupLoading(groupKey, true);
    try {
      const res = await api.get("/processes/instances/open", {
        params: { processGroupKey: groupKey },
      });
      const loadedItems = Array.isArray(res.data) ? res.data : [];
      if (!loadedItems.length) {
        return;
      }

      setItems((prev) => {
        const withoutSummary = prev.filter(
          (item) => !(item?.isProcessTaskSummary && item?.processGroupKey === groupKey)
        );
        const existingKeys = new Set(withoutSummary.map((item) => getInstanceKey(item)).filter(Boolean));
        const merged = [...withoutSummary];
        loadedItems.forEach((item) => {
          const key = getInstanceKey(item);
          if (!key || !existingKeys.has(key)) {
            merged.push(item);
            if (key) existingKeys.add(key);
          }
        });
        return merged;
      });
    } finally {
      setGroupLoading(groupKey, false);
    }
  }, [loadingGroups, setGroupLoading]);

  const toggleGroupExpansion = useCallback((groupKey, group) => {
    if (!groupKey) {
      return;
    }

    const shouldExpand = !expandedGroupKeys.has(groupKey);
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });

    const needsLazyLoad = Boolean(
      shouldExpand &&
      group?.isSummary &&
      group.itemsCount > TASK_SUMMARY_INLINE_LIMIT
    );
    if (needsLazyLoad) {
      loadProcessTasks(groupKey);
    }
  }, [expandedGroupKeys, loadProcessTasks]);

  useEffect(() => {
    if (activePanel !== "overview") return;
    let cancelled = false;
    setTasksLoading(true);
    preloadedProcessGroupsRef.current.clear();
    setTasksLoadProgress(12);
    const progressTimer = window.setInterval(() => {
      setTasksLoadProgress((current) => Math.min(current + 10, 88));
    }, 350);
    api
      .get("/processes/instances/open", {
        params: { summary: 1, inlineLimit: TASK_SUMMARY_INLINE_LIMIT },
      })
      .then((res) => {
        if (!cancelled) {
          setTasksLoadProgress(96);
          setItems(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) {
          window.clearInterval(progressTimer);
          setTasksLoadProgress(100);
          window.setTimeout(() => setTasksLoadProgress(0), 250);
          setTasksLoading(false);
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new window.CustomEvent("bpm:home-tasks-priority-ready", {
                detail: { source: "HomeDashboard", at: Date.now() },
              })
            );
          }
        }
      });
    return () => {
      cancelled = true;
      window.clearInterval(progressTimer);
    };
  }, [activePanel, panelRefreshToken, refreshKey]);
  useEffect(() => {
    if (activePanel !== "overview") return;
    let cancelled = false;
    api
      .get("/processes/instances/my-counts")
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setCounts({
          total: Number(data.total) || 0,
          overdue: Number(data.overdue) || 0,
          open: Number(data.open) || 0,
          completed:
            Number(data.completed) ||
            Math.max((Number(data.total) || 0) - (Number(data.open) || 0), 0),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setCounts({ total: 0, overdue: 0, open: 0, completed: 0 });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activePanel, panelRefreshToken, refreshKey]);

  const loadCalendarHighlights = useCallback(async () => {
    setCalendarLoading(true);
    setCalendarError("");
    try {
      const res = await api.get("/calendar/summary");
      const data = res.data || {};
      setTodayMessages(Array.isArray(data.todayMessages) ? data.todayMessages : []);
    } catch (error) {
      setTodayMessages([]);
      setCalendarError(
        error?.response?.data?.error || "Não foi possível carregar seus lembretes de hoje."
      );
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activePanel !== "overview") return;
    loadCalendarHighlights();
  }, [activePanel, loadCalendarHighlights, panelRefreshToken, refreshKey]);

  const loadAdvancedIndicators = useCallback(async () => {
    setAdvancedLoading(true);
    setAdvancedError("");
    setCompletedHistoryCount(0);
    setCompletedHistoryComplete(false);
    const warnings = [];

    try {
      const [openResult, completedResult] = await Promise.allSettled([
        api.get("/processes/instances/open"),
        fetchAllCompletedIndicators(api, {
          limitDays: COMPLETED_ANALYTICS_DAYS,
          pageSize: COMPLETED_ANALYTICS_PAGE_SIZE,
          onProgress: ({ loaded }) => setCompletedHistoryCount(loaded),
        }),
      ]);

      const openItems =
        openResult.status === "fulfilled" && Array.isArray(openResult.value?.data)
          ? openResult.value.data
          : [];
      const completedItems =
        completedResult.status === "fulfilled" && Array.isArray(completedResult.value)
          ? completedResult.value
          : [];

      setGlobalOpenItems(openItems);
      setGlobalCompletedItems(completedItems);

      const openFailed = openResult.status === "rejected";
      const completedFailed = completedResult.status === "rejected";
      setCompletedHistoryComplete(!completedFailed);
      if (completedFailed) setCompletedHistoryCount(0);

      if (openFailed) {
        warnings.push("Não foi possível carregar as solicitações em andamento (permissão necessária).");
      }

      if (completedFailed) {
        warnings.push("Não foi possível carregar o histórico completo de solicitações concluídas.");
      }

      if (openFailed && completedFailed) {
        warnings.push("Os indicadores de solicitações serão exibidos apenas com dados de processos e formulários disponíveis.");
      }
    } catch (error) {
      setGlobalOpenItems([]);
      setGlobalCompletedItems([]);
      setCompletedHistoryCount(0);
      setCompletedHistoryComplete(false);
      setAdvancedError("Não foi possível carregar os indicadores de solicitações.");
      setAdvancedWarnings([]);
      setAdvancedLoading(false);
      return;
    }

    try {
      const res = await api.get("/processes");
      setGlobalProcesses(res.data || []);
    } catch (error) {
      try {
        const fallback = await api.get("/processes/available");
        setGlobalProcesses(fallback.data || []);
        warnings.push("Os totais de processos consideram apenas os fluxos disponíveis para você.");
      } catch (innerError) {
        setGlobalProcesses([]);
        warnings.push("Não foi possível carregar a lista de processos cadastrados.");
      }
    }

    try {
      const res = await api.get("/forms");
      setGlobalForms(res.data || []);
    } catch (error) {
      setGlobalForms([]);
      warnings.push("Não foi possível carregar os formulários publicados (permissão necessária).");
    }

    setAdvancedWarnings(warnings);
    setAdvancedUpdatedAt(new Date());
    setAdvancedLoading(false);
  }, []);

  useEffect(() => {
    if (activePanel !== "insights") return;
    loadAdvancedIndicators();
  }, [activePanel, loadAdvancedIndicators, panelRefreshToken, refreshKey]);

  const determineAssignmentScopes = useCallback(
    (instance) => {
      const scopes = new Set();
      if (!instance) return scopes;

      const normalizedUserId = normalizeId(user?.id);
      if (!normalizedUserId) return scopes;

      const assigneeId = normalizeId(instance.currentAssignee?.id);
      const hasEffectiveDifferentAssignee =
        assigneeId && assigneeId !== normalizedUserId && !instance.isPotentialResponsible;
      if (hasEffectiveDifferentAssignee) {
        return scopes;
      }

      if ((assigneeId && assigneeId === normalizedUserId && instance.isCurrentAssignee) || instance.isCurrentAssignee) {
        scopes.add("mine");
      }

      const requesterId = normalizeId(instance.startedBy?.id);
      const normalizedGroupId = normalizeId(user?.group?.id);
      const normalizedSectorId = normalizeId(user?.sector?.id);

      const responsibleSources = [instance.stepResponsibles];

      for (const source of responsibleSources) {
        if (!source) continue;
        const entry = source[String(instance.currentStep)];
        if (!entry) continue;

        const { userIds, groupIds, sectorIds, requester } = extractResponsible(entry);
        const normalizedUserIds = toNormalizedArray(userIds);
        const normalizedGroupIds = toNormalizedArray(groupIds);
        const normalizedSectorIds = toNormalizedArray(sectorIds);

        if (normalizedUserIds.includes(normalizedUserId)) {
          scopes.add("mine");
        }

        if (
          normalizedGroupId &&
          normalizedGroupIds.includes(normalizedGroupId)
        ) {
          scopes.add("sector");
        }

        if (
          normalizedSectorId &&
          normalizedSectorIds.includes(normalizedSectorId)
        ) {
          scopes.add("sector");
        }

        if (
          requester &&
          requesterId &&
          requesterId === normalizedUserId
        ) {
          scopes.add("mine");
        }
      }

      if (!scopes.size && instance.isPotentialResponsible) {
        scopes.add("sector");
      }

      return scopes;
    },
    [extractResponsible, user]
  );

  const itemMetadata = useMemo(() => {
    const scoped = [];
    const scopesMap = new Map();
    const statusMap = new Map();
    const counts = { total: 0, onTime: 0, alert: 0, late: 0 };

    items.forEach((instance) => {
      const scopes = determineAssignmentScopes(instance);
      if (!scopes.size) {
        return;
      }

      scoped.push(instance);
      const key = getInstanceKey(instance);

      if (key) {
        scopesMap.set(key, scopes);
      }

      const { status } = evaluateTaskDeadline(instance);
      if (key) {
        statusMap.set(key, status);
      }

      const taskWeight = Math.max(Number(instance?.taskCount) || 0, 1);
      counts.total += taskWeight;
      if (status === "late") {
        counts.late += taskWeight;
      } else if (status === "alert") {
        counts.alert += taskWeight;
      } else {
        counts.onTime += taskWeight;
      }
    });

    return {
      scopedItems: scoped,
      scopesMap,
      taskCounts: counts,
      taskStatusMap: statusMap,
    };
  }, [determineAssignmentScopes, evaluateTaskDeadline, items]);

  const { scopesMap, taskCounts, taskStatusMap } = itemMetadata;

  const getTaskStatusForInstance = useCallback(
    (instance) => {
      const key = getInstanceKey(instance);
      if (key && taskStatusMap.has(key)) {
        return taskStatusMap.get(key);
      }
      return evaluateTaskDeadline(instance).status;
    },
    [evaluateTaskDeadline, taskStatusMap]
  );

  const matchesTaskFilter = useCallback(
    (instance) => {
      if (taskFilter === "all") {
        return true;
      }

      const status = getTaskStatusForInstance(instance);

      if (taskFilter === "onTime") {
        return status === "onTime";
      }
      if (taskFilter === "alert") {
        return status === "alert";
      }
      if (taskFilter === "late") {
        return status === "late";
      }

      return true;
    },
    [getTaskStatusForInstance, taskFilter]
  );

  const toDeadlineTime = useCallback((value) => {
    if (!value) return Number.POSITIVE_INFINITY;
    const date = new Date(value);
    const time = date.getTime();
    return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
  }, []);

  const compareByDeadline = useCallback(
    (a, b) => toDeadlineTime(a?.currentStepDeadline) - toDeadlineTime(b?.currentStepDeadline),
    [toDeadlineTime]
  );

  const getProcessGroupKey = useCallback((instance) => {
    if (!instance) return null;
    const candidateValues = [
      instance.processGroupKey,
      instance.process?.id,
      instance.processId,
      instance.process?.processId,
      instance.process?.key,
      instance.process?.code,
      instance.process?.identifier,
      instance.process?.acronym,
      instance.process?.shortName,
    ];

    for (const value of candidateValues) {
      const normalized = normalizeId(value);
      if (normalized) {
        if (normalized.startsWith("process-") || normalized.startsWith("process-name-") || normalized.startsWith("instance-")) {
          return normalized;
        }
        return `process-${normalized}`;
      }
    }

    const name = typeof instance.process?.name === "string" ? instance.process.name.trim() : "";
    if (name) {
      return `process-name-${name.toLowerCase()}`;
    }

    const fallbackKey = getInstanceKey(instance);
    return fallbackKey ? `instance-${fallbackKey}` : null;
  }, []);

  const groupAssignmentsByProcess = useCallback(
    (assignments) => {
      const map = new Map();

      assignments.forEach((instance) => {
        const key = getProcessGroupKey(instance) || `instance-${instance?.id}`;
        if (!map.has(key)) {
          map.set(key, { key, items: [], isSummary: false, itemsCount: 0 });
        }
        const group = map.get(key);
        group.items.push(instance);
        group.isSummary = group.isSummary || Boolean(instance?.isProcessTaskSummary);
        group.itemsCount = Math.max(group.itemsCount || 0, Number(instance?.taskCount) || 0);
      });

      const groups = Array.from(map.values());
      groups.forEach((group) => {
        group.items.sort(compareByDeadline);
        group.itemsCount = Math.max(group.itemsCount || 0, group.items.length);
      });

      groups.sort((a, b) => {
        const earliestA = a.items.length ? toDeadlineTime(a.items[0].currentStepDeadline) : Number.POSITIVE_INFINITY;
        const earliestB = b.items.length ? toDeadlineTime(b.items[0].currentStepDeadline) : Number.POSITIVE_INFINITY;
        return earliestA - earliestB;
      });

      return groups;
    },
    [compareByDeadline, getProcessGroupKey, toDeadlineTime]
  );

  const { directAssignments, sectorAssignments } = useMemo(() => {
    const direct = [];
    const sectorOnly = [];

    items.forEach((instance) => {
      if (!matchesTaskFilter(instance)) {
        return;
      }

      const instanceKey = getInstanceKey(instance);
      const scopes = instanceKey ? scopesMap.get(instanceKey) : determineAssignmentScopes(instance);
      if (scopes?.has("mine")) {
        direct.push(instance);
        return;
      }

      if (scopes?.has("sector")) {
        sectorOnly.push(instance);
      }
    });

    direct.sort(compareByDeadline);
    sectorOnly.sort(compareByDeadline);

    return { directAssignments: direct, sectorAssignments: sectorOnly };
  }, [compareByDeadline, determineAssignmentScopes, items, matchesTaskFilter, scopesMap]);

  const directGroups = useMemo(
    () => groupAssignmentsByProcess(directAssignments),
    [directAssignments, groupAssignmentsByProcess]
  );

  const sectorGroups = useMemo(
    () => groupAssignmentsByProcess(sectorAssignments),
    [groupAssignmentsByProcess, sectorAssignments]
  );


  useEffect(() => {
    if (activePanel !== "overview" || tasksLoading) {
      return;
    }

    const summaryGroupKeys = items
      .filter((item) => item?.isProcessTaskSummary)
      .map((item) => getProcessGroupKey(item) || item?.processGroupKey)
      .filter(Boolean);

    summaryGroupKeys.forEach((groupKey) => {
      if (preloadedProcessGroupsRef.current.has(groupKey)) {
        return;
      }
      preloadedProcessGroupsRef.current.add(groupKey);
      loadProcessTasks(groupKey);
    });
  }, [activePanel, getProcessGroupKey, items, loadProcessTasks, tasksLoading]);

  const directHighlights = useMemo(
    () => directGroups.slice(0, 4),
    [directGroups]
  );

  const sectorHighlights = useMemo(
    () => sectorGroups.slice(0, 4),
    [sectorGroups]
  );

  const hasSectorContext = Boolean(user?.sector?.id);

  const indicatorProcessOptions = useMemo(
    () =>
      buildIndicatorProcessOptions(globalProcesses, [
        ...globalOpenItems,
        ...globalCompletedItems,
      ]),
    [globalCompletedItems, globalOpenItems, globalProcesses]
  );

  const indicatorData = useMemo(
    () =>
      buildIndicatorDataset({
        openItems: globalOpenItems,
        completedItems: globalCompletedItems,
        processValue: indicatorProcess,
        periodDays: indicatorPeriodDays,
        status: indicatorStatus,
      }),
    [
      globalCompletedItems,
      globalOpenItems,
      indicatorPeriodDays,
      indicatorProcess,
      indicatorStatus,
    ]
  );

  const {
    allTrackedInstances,
    completedInPeriod,
    filteredOpenItems,
    overdueOpenCount,
    completionRate,
    averageCompletionMs,
  } = indicatorData;

  const aggregatedByMonth = useMemo(() => {
    const map = new Map();
    allTrackedInstances.forEach((instance) => {
      const started = new Date(instance.startedAt);
      if (Number.isNaN(started.getTime())) return;
      const key = `${started.getFullYear()}-${String(started.getMonth() + 1).padStart(2, "0")}`;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [allTrackedInstances]);

  const aggregatedByDate = useMemo(() => {
    const map = new Map();
    allTrackedInstances.forEach((instance) => {
      const started = new Date(instance.startedAt);
      if (Number.isNaN(started.getTime())) return;
      const key = `${started.getFullYear()}-${String(started.getMonth() + 1).padStart(2, "0")}-${String(
        started.getDate()
      ).padStart(2, "0")}`;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [allTrackedInstances]);

  const monthlyTrend = useMemo(() => {
    const points = [];
    const labels = [];
    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}`;
      const count = aggregatedByMonth.get(key) || 0;
      const label = monthDate
        .toLocaleDateString("pt-BR", { month: "short" })
        .replace(".", "")
        .toUpperCase();
      labels.push(label);
      points.push(count);
    }
    const chartWidth = 520;
    const chartHeight = 200;
    const padding = 40;
    const maxValue = Math.max(1, ...points);
    const mappedPoints = points.map((value, index) => {
      const x =
        points.length === 1
          ? chartWidth / 2
          : (index / (points.length - 1)) * (chartWidth - padding * 2) + padding;
      const y =
        chartHeight - padding - (value / maxValue) * Math.max(chartHeight - padding * 2, 1);
      return { x, y, value };
    });
    const areaPath = mappedPoints.length
      ? `M ${mappedPoints[0].x} ${mappedPoints[0].y} ${mappedPoints
          .slice(1)
          .map((p) => `L ${p.x} ${p.y}`)
          .join(" ")} L ${mappedPoints[mappedPoints.length - 1].x} ${chartHeight - padding} L ${
          mappedPoints[0].x
        } ${chartHeight - padding} Z`
      : "";
    return {
      labels,
      points: mappedPoints,
      areaPath,
      chart: { width: chartWidth, height: chartHeight, padding },
      total: points.reduce((acc, curr) => acc + curr, 0),
    };
  }, [aggregatedByMonth]);

  const periodTrend = useMemo(() => {
    const windowDays = Math.min(Math.max(recentDays, 1), 30);
    const now = new Date();
    const buckets = [];
    for (let i = windowDays - 1; i >= 0; i -= 1) {
      const day = new Date(now);
      day.setHours(0, 0, 0, 0);
      day.setDate(now.getDate() - i);
      const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(
        day.getDate()
      ).padStart(2, "0")}`;
      const count = aggregatedByDate.get(key) || 0;
      const label = day
        .toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
        .replace(".", "");
      buckets.push({ label, count });
    }
    const max = Math.max(...buckets.map((bucket) => bucket.count), 1);
    const total = buckets.reduce((acc, bucket) => acc + bucket.count, 0);
    return { buckets, max, windowDays, total };
  }, [aggregatedByDate, recentDays]);

  const avgCompletionAll = averageCompletionMs;

  const formsWithExternal = useMemo(() => {
    return globalForms.filter((form) => form?.externalAccessEnabled).length;
  }, [globalForms]);

  const processCategoryDistribution = useMemo(() => {
    if (!globalProcesses.length) return [];
    const accumulator = globalProcesses.reduce((acc, process) => {
      const key = process?.category?.trim() || "Sem categoria";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const entries = Object.entries(accumulator).sort((a, b) => b[1] - a[1]);
    const total = globalProcesses.length || 1;
    return entries.map(([name, value]) => ({
      name,
      value,
      percentage: Math.round((value / total) * 100),
    }));
  }, [globalProcesses]);

  const topProcessesByVolume = useMemo(() => {
    if (!allTrackedInstances.length) return [];
    const accumulator = allTrackedInstances.reduce((acc, instance) => {
      const key = instance.process?.name || "Processo";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const entries = Object.entries(accumulator).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((acc, [, value]) => acc + value, 0) || 1;
    return entries.slice(0, 6).map(([name, value]) => ({
      name,
      value,
      percentage: Math.round((value / total) * 100),
    }));
  }, [allTrackedInstances]);

  const globalStatusBreakdown = useMemo(() => {
    if (!filteredOpenItems.length) {
      return [
        { label: "Sem solicitações em aberto", value: 0, color: "bg-blue-200", hex: "#bfdbfe", percentage: 0 },
      ];
    }
    const withDeadline = filteredOpenItems.filter((instance) => instance.currentStepDeadline);
    const onTime = withDeadline.filter((instance) => {
      const deadline = new Date(instance.currentStepDeadline);
      return !Number.isNaN(deadline.getTime()) && deadline.getTime() >= Date.now();
    }).length;
    const late = withDeadline.length - onTime;
    const noDeadline = filteredOpenItems.length - withDeadline.length;
    const total = filteredOpenItems.length || 1;
    return [
      {
        label: "Dentro do prazo",
        value: onTime,
        color: "bg-emerald-500",
        hex: "#10b981",
        percentage: Math.round((onTime / total) * 100),
      },
      {
        label: "Em atraso",
        value: late,
        color: "bg-rose-500",
        hex: "#f43f5e",
        percentage: Math.round((late / total) * 100),
      },
      {
        label: "Sem prazo",
        value: noDeadline,
        color: "bg-slate-400",
        hex: "#94a3b8",
        percentage: Math.round((noDeadline / total) * 100),
      },
    ];
  }, [filteredOpenItems]);

  const statusDonutBackground = useMemo(
    () => buildStatusDonutBackground(globalStatusBreakdown),
    [globalStatusBreakdown]
  );

  const averageCompletionLabel = useMemo(() => {
    if (!avgCompletionAll) return "Sem histórico suficiente";
    return `Tempo médio de ciclo: ${formatDuration(avgCompletionAll)}`;
  }, [avgCompletionAll]);

  const overdueRatio = filteredOpenItems.length
    ? Math.round((overdueOpenCount / filteredOpenItems.length) * 100)
    : 0;

  const indicatorStatusLabel =
    indicatorStatus === "open"
      ? "Solicitações abertas"
      : indicatorStatus === "completed"
        ? "Solicitações concluídas"
        : "Abertas e concluídas";

  const selectedProcessLabel =
    indicatorProcessOptions.find((option) => option.value === indicatorProcess)?.label ||
    "Todos os processos";

  const advancedCards = [
    {
      id: "open",
      label: "Tarefas em aberto",
      value: filteredOpenItems.length,
      icon: <FaTasks className="text-2xl" />,
      description: `${selectedProcessLabel} · fila atual`,
      surface: "from-violet-600 to-indigo-700",
      iconSurface: "bg-white/15 text-violet-50",
    },
    {
      id: "late",
      label: "Tarefas atrasadas",
      value: overdueOpenCount,
      icon: <FaExclamationTriangle className="text-2xl" />,
      description: `${overdueRatio}% das tarefas abertas`,
      surface: "from-rose-500 to-orange-500",
      iconSurface: "bg-white/15 text-rose-50",
    },
    {
      id: "completed",
      label: "Concluídas no período",
      value: completedInPeriod.length,
      icon: <FaCheckCircle className="text-2xl" />,
      description: `Últimos ${indicatorPeriodDays} dias`,
      surface: "from-emerald-500 to-teal-600",
      iconSurface: "bg-white/15 text-emerald-50",
    },
    {
      id: "rate",
      label: "Taxa de conclusão",
      value: `${completionRate}%`,
      icon: <FaChartLine className="text-2xl" />,
      description: averageCompletionLabel,
      surface: "from-sky-500 to-blue-700",
      iconSurface: "bg-white/15 text-sky-50",
    },
  ];

  const handleResetIndicatorFilters = () => {
    setIndicatorPeriodDays(90);
    setIndicatorProcess("all");
    setIndicatorStatus("all");
    setRecentDays(7);
  };

  const openInstance = (inst) => {
    if (openTab) {
      openTab(
        `instance-${inst.id}`,
        `Solicitação ${inst.id}`,
        ProcessRunner,
        { instanceId: inst.id, returnToTabId: "home" }
      );
    }
  };

  const handleClearTaskFilter = useCallback(() => {
    setTaskFilter("all");
  }, [setTaskFilter]);

  const getProcessIdentifierLabel = (item) => {
    const rawIdentifier =
      item?.process?.identifier ||
      item?.process?.code ||
      item?.process?.key ||
      item?.process?.acronym ||
      item?.process?.shortName ||
      null;

    if (typeof rawIdentifier === "string" && rawIdentifier.trim()) {
      return rawIdentifier.trim();
    }

    const rawId = item?.process?.id ?? item?.processId ?? item?.process?.processId ?? null;

    if (rawId === null || rawId === undefined) {
      return null;
    }

    const normalized = String(rawId).trim();
    if (!normalized) {
      return null;
    }

    if (/^\d+$/.test(normalized)) {
      return null;
    }

    return normalized.startsWith("#") ? normalized : `#${normalized}`;
  };

  const getHighlightInfo = (item) => {
    const highlightValue = (() => {
      const raw = item?.dashboardHighlight?.value;
      if (typeof raw !== "string") return null;
      const trimmed = raw.trim();
      if (!trimmed) return null;
      const linkedFieldMatch = trimmed.match(/^\d+:[^:]+:([\s\S]*)$/);
      const displayValue = linkedFieldMatch ? linkedFieldMatch[1].trim() : trimmed;
      return displayValue || null;
    })();

    return { value: highlightValue, label: null, title: highlightValue || undefined };
  };

  const getDeadlineLabel = (item) => {
    if (!item?.currentStepDeadline) {
      return null;
    }

    const deadlineDate = new Date(item.currentStepDeadline);
    if (Number.isNaN(deadlineDate.getTime())) {
      return null;
    }

    const datePart = deadlineDate.toLocaleDateString("pt-BR");
    const timePart = deadlineDate.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart} às ${timePart}`;
  };

  const getStatusStyles = (status) => {
    const normalizedStatusLabel = status.label?.toLowerCase?.() || "";
    const isCompactBadge =
      normalizedStatusLabel === "prazo excedido" ||
      normalizedStatusLabel === "sem prazo" ||
      normalizedStatusLabel.startsWith("vence em");

    const base = `${
      isCompactBadge
        ? "border border-blue-500/60 bg-white/80 text-blue-800"
        : "border bg-transparent"
    } backdrop-blur-sm shadow-sm`;

    let statusBadgeClass = base;
    if (!isCompactBadge) {
      if (status.color?.includes("rose")) {
        statusBadgeClass = `${base} border-rose-600 text-rose-700`;
      } else if (status.color?.includes("amber")) {
        statusBadgeClass = `${base} border-amber-600 text-amber-700`;
      } else if (status.color?.includes("emerald")) {
        statusBadgeClass = `${base} border-emerald-600 text-emerald-700`;
      } else {
        statusBadgeClass = `${base} border-sky-600 text-sky-700`;
      }
    }

    const deadlineTextClass = (() => {
      if (status.color?.includes("rose")) {
        return "text-rose-600";
      }
      if (status.color?.includes("amber")) {
        return "text-amber-600";
      }
      if (status.color?.includes("emerald")) {
        return "text-emerald-600";
      }
      return "text-blue-700";
    })();

    return { statusBadgeClass, isCompactBadge, deadlineTextClass };
  };

  const renderProcessGroup = (group) => {
    if (!group?.items?.length) {
      return null;
    }

    const representative = group.items[0];
    const groupKey =
      group.key || getProcessGroupKey(representative) || getInstanceKey(representative) || `instance-${representative?.id}`;
    const itemsCount = Math.max(Number(group.itemsCount) || 0, group.items.length);
    const isGroupLoading = loadingGroups.has(groupKey);
    const isExpandable = itemsCount > 1;
    const isExpanded = !isExpandable || expandedGroupKeys.has(groupKey);
    const hasMultipleItems = itemsCount > 1;
    const visibleItemsCount = Math.min(visibleTasksByGroup[groupKey] || TASKS_PER_PROCESS_PAGE, itemsCount);
    const visibleItems = group.items.slice(0, visibleItemsCount);
    const hiddenItemsCount = Math.max(itemsCount - visibleItemsCount, 0);

    const processIdentifierLabel = getProcessIdentifierLabel(representative);

    if (!isExpandable) {
      const status = getDeadlineStatus(representative);
      const { value: highlightValue, label: highlightDisplayLabel, title: highlightTitle } =
        getHighlightInfo(representative);
      const { statusBadgeClass, isCompactBadge } = getStatusStyles(status);

      return (
        <div key={groupKey} className="dashboard-process-wrapper rounded-3xl bg-blue-200 p-[1.5px]">
          <div className="dashboard-process-card group flex flex-col overflow-hidden rounded-2xl border border-blue-50/80 bg-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg">
            <div className="relative -mx-[1.5px] -mt-[1.5px] overflow-hidden rounded-t-2xl bg-gradient-to-r from-blue-500/40 via-blue-500/20 to-transparent px-4 py-2 text-blue-900">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <h4 className="dashboard-process-name text-lg font-semibold leading-snug text-blue-950">
                    {representative.process?.name || "Processo"}
                  </h4>
                  {processIdentifierLabel && (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-600/90">
                      {processIdentifierLabel}
                    </span>
                  )}
                  {hasMultipleItems && (
                    <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-blue-800">
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] text-blue-600">
                        {itemsCount} {itemsCount === 1 ? "tarefa" : "tarefas"}
                      </span>
                    </span>
                  )}
                  {highlightValue && (
                    <span
                      title={highlightTitle}
                      className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-blue-100 bg-white/40 px-2 py-0.5 text-[10px] font-semibold text-blue-800 backdrop-blur-sm"
                    >
                      <span className="text-[8px] font-semibold uppercase tracking-[0.35em] text-blue-500">
                        {highlightDisplayLabel}
                      </span>
                      <span className="max-w-[13rem] truncate">{highlightValue}</span>
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`inline-flex items-center rounded-full ${
                      isCompactBadge ? "px-1 text-[8px]" : "px-2 text-[10px]"
                    } py-0.5 font-semibold uppercase tracking-[0.25em] ${statusBadgeClass}`}
                  >
                    {status.label}
                  </span>
                </div>
              </div>
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/40 blur-2xl transition group-hover:scale-110" />
            </div>
            <div className="divide-y divide-blue-100/80 bg-blue-50/40">
              {group.items.map((item) => {
                const itemStatus = getDeadlineStatus(item);
                const { statusBadgeClass: itemBadgeClass, isCompactBadge: itemCompact, deadlineTextClass: itemDeadlineClass } =
                  getStatusStyles(itemStatus);
                const itemHighlight = getHighlightInfo(item);
                const itemStartedAt = new Date(item.startedAt);
                const itemStartedLabel = Number.isNaN(itemStartedAt.getTime()) ? "-" : itemStartedAt.toLocaleString();
                const hasItemDeadline = Boolean(item?.currentStepDeadline);
                const itemDeadlineLabel = hasItemDeadline ? getDeadlineLabel(item) : itemStatus.label;

                const showItemStatusBadge = hasMultipleItems && itemStatus.label !== NO_DEADLINE_LABEL;
                const showItemHighlight = hasMultipleItems && itemHighlight.value;
                const showItemDeadline = hasItemDeadline || hasMultipleItems;

                return (
                  <div
                    key={item.id}
                    className="flex flex-col gap-3 px-4 py-3 text-[11px] text-slate-600 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50/70 px-2 py-0.5 font-semibold text-blue-700">
                        <span className="font-bold text-blue-800">#{String(item.id).padStart(4, "0")}</span>
                      </span>
                      {showItemStatusBadge && (
                        <span
                          className={`inline-flex items-center rounded-full ${
                            itemCompact ? "px-1 text-[8px]" : "px-2 text-[10px]"
                          } py-0.5 font-semibold uppercase tracking-[0.25em] ${itemBadgeClass}`}
                        >
                          {itemStatus.label}
                        </span>
                      )}
                      {showItemHighlight && (
                        <span
                          title={itemHighlight.title}
                          className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-blue-100 bg-white/60 px-2 py-0.5 text-[10px] font-semibold text-blue-800 backdrop-blur"
                        >
                          <span className="text-[8px] font-semibold uppercase tracking-[0.35em] text-blue-500">
                            {itemHighlight.label}
                          </span>
                          <span className="max-w-[12rem] truncate">{itemHighlight.value}</span>
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5 text-slate-700">
                        <FaCalendarAlt className="text-blue-500" />
                        <span>
                          <span className="font-semibold text-slate-900">Início:</span> {itemStartedLabel}
                        </span>
                      </span>
                      {showItemDeadline && itemDeadlineLabel && (
                        <span className={`inline-flex items-center gap-1.5 ${itemDeadlineClass}`}>
                          <FaStopwatch className={`text-[13px] ${itemDeadlineClass}`} />
                          <span>
                            <span className="font-semibold">Prazo:</span> {itemDeadlineLabel}
                          </span>
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openInstance(item)}
                      className="dashboard-task-open-button inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700"
                    >
                      <FaRegHandPaper className="text-sm" />
                      Abrir
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={groupKey} className="dashboard-process-wrapper rounded-3xl bg-blue-200 p-[1.5px]">
        <div className="dashboard-process-card flex flex-col overflow-hidden rounded-2xl border border-blue-50/80 bg-white shadow-md">
          <button
            type="button"
            onClick={() => toggleGroupExpansion(groupKey, { ...group, itemsCount })}
            aria-expanded={isExpanded}
            className="relative flex items-center justify-between gap-3 bg-gradient-to-r from-blue-500/40 via-blue-500/20 to-transparent px-4 py-3 text-left text-blue-950 transition hover:bg-blue-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2"
          >
            <div className="flex flex-col gap-1">
              <h4 className="dashboard-process-name text-lg font-semibold leading-snug">{representative.process?.name || "Processo"}</h4>
              {processIdentifierLabel && (
                <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-700/90">
                  {processIdentifierLabel}
                </span>
              )}
              {itemsCount > 1 && (
                <span className="inline-flex flex-wrap items-center gap-2 text-[11px] font-semibold text-blue-800">
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] text-blue-600">
                    {itemsCount} tarefas
                  </span>
                  <span className="rounded-full bg-white/60 px-2 py-0.5 text-[10px] text-blue-700">
                    Mostrando {visibleItemsCount} de {itemsCount}
                  </span>
                </span>
              )}
            </div>
            <span
              className={`inline-flex items-center justify-center rounded-full border border-blue-200 bg-white/70 p-1 text-blue-700 transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            >
              <FaChevronDown className="text-xs" />
            </span>
          </button>
          <div className={`${isExpanded ? "block" : "hidden"}`}>
            {isGroupLoading && (
              <div className="border-t border-blue-100 bg-blue-50/70 px-4 py-3">
                <div className="h-2 overflow-hidden rounded-full bg-blue-100">
                  <div className="h-full w-2/3 animate-pulse rounded-full bg-gradient-to-r from-blue-400 to-sky-400" />
                </div>
              </div>
            )}
            {!isGroupLoading && visibleItems.map((item) => {
              const itemStatus = getDeadlineStatus(item);
              const { deadlineTextClass: itemDeadlineClass } = getStatusStyles(itemStatus);
              const itemHighlight = getHighlightInfo(item);
              const itemStartedAt = new Date(item.startedAt);
              const itemStartedLabel = Number.isNaN(itemStartedAt.getTime()) ? "-" : itemStartedAt.toLocaleString();
              const itemDeadlineLabel = getDeadlineLabel(item) || itemStatus.label;

              return (
                <div
                  key={item.id}
                  className="border-t border-blue-100 bg-white px-4 py-3 text-[11px] text-slate-600"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-700">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center justify-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        #{String(item.id).padStart(4, "0")}
                      </span>
                      {itemHighlight.value && (
                        <span
                          title={itemHighlight.title}
                          className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50/80 px-2 py-0.5 text-[10px] font-semibold text-blue-800"
                        >
                          <span className="text-[8px] font-semibold uppercase tracking-[0.35em] text-blue-500">
                            {itemHighlight.label}
                          </span>
                          <span className="max-w-[12rem] truncate">{itemHighlight.value}</span>
                        </span>
                      )}
                    </div>
                    {itemStatus.label !== NO_DEADLINE_LABEL && (
                      <span className={`inline-flex items-center gap-1 font-semibold ${itemDeadlineClass}`}>
                        <FaStopwatch className={`text-[13px] ${itemDeadlineClass}`} />
                        {itemStatus.label}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-slate-600">
                      <FaCalendarAlt className="text-blue-500" />
                      <span>
                        <span className="font-semibold text-slate-900">Início:</span> {itemStartedLabel}
                      </span>
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 ${itemDeadlineClass}`}>
                        <FaStopwatch className={`text-[13px] ${itemDeadlineClass}`} />
                        <span>
                          <span className="font-semibold">Prazo:</span> {itemDeadlineLabel}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => openInstance(item)}
                        className="dashboard-task-open-button inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700"
                      >
                        <FaRegHandPaper className="text-sm" />
                        Abrir
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {!isGroupLoading && hiddenItemsCount > 0 && (
              <div className="border-t border-blue-100 bg-blue-50/60 px-4 py-3 text-center">
                <button
                  type="button"
                  onClick={() => showMoreGroupTasks(groupKey, itemsCount)}
                  className="inline-flex items-center justify-center rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50 hover:text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2"
                >
                  Ver mais {Math.min(TASKS_PER_PROCESS_PAGE, hiddenItemsCount)}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAssignmentCard = (item) => {
    const group = Array.isArray(item?.items) ? item : { key: item?.id ?? getInstanceKey(item), items: [item] };
    return renderProcessGroup(group);
  };

  const firstName = user?.name ? user.name.split(" ")[0] : "";
  const [dashboardAiGreetingIndex, setDashboardAiGreetingIndex] = useState(() => Math.floor(Date.now() / 1000));
  const [typedDashboardAiGreeting, setTypedDashboardAiGreeting] = useState("");
  const [dashboardAiCursorFrame, setDashboardAiCursorFrame] = useState(false);
  const dashboardAiGreeting = buildDashboardAiGreeting(firstName, dashboardAiGreetingIndex);

  useEffect(() => {
    setDashboardAiGreetingIndex((current) => current + 1);
    const timer = window.setInterval(() => {
      setDashboardAiGreetingIndex((current) => current + 1);
    }, 45000);
    return () => window.clearInterval(timer);
  }, [firstName]);

  useEffect(() => {
    setTypedDashboardAiGreeting("");
    let characterIndex = 0;
    const typingTimer = window.setInterval(() => {
      characterIndex += 1;
      setTypedDashboardAiGreeting(dashboardAiGreeting.slice(0, characterIndex));
      if (characterIndex >= dashboardAiGreeting.length) {
        window.clearInterval(typingTimer);
      }
    }, 42);
    return () => window.clearInterval(typingTimer);
  }, [dashboardAiGreeting]);

  useEffect(() => {
    const cursorTimer = window.setInterval(() => {
      setDashboardAiCursorFrame((current) => !current);
    }, 360);
    return () => window.clearInterval(cursorTimer);
  }, []);

  useEffect(() => {
    const handlePageTyping = (event) => {
      if (isAiPromptActive || aiPromptPending || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      const isEditableTarget =
        target?.isContentEditable ||
        ["input", "textarea", "select", "button", "a"].includes(tagName);

      if (isEditableTarget || event.key.length !== 1) {
        return;
      }

      event.preventDefault();
      setAiPrompt(event.key);
      setIsAiPromptActive(true);
    };

    window.addEventListener("keydown", handlePageTyping);
    return () => window.removeEventListener("keydown", handlePageTyping);
  }, [aiPromptPending, isAiPromptActive]);

  const handleDashboardAiSubmit = async (event) => {
    event.preventDefault();
    const prompt = aiPrompt.trim();
    if (!prompt || aiPromptPending) {
      return;
    }
    setAiPromptPending(true);
    try {
      const reply = await getAssistantReply(prompt, { user });
      if (reply?.action) {
        triggerAssistantAction(reply.action);
        if (reply.action.tabId === "project-bank" && /novo projeto|criar projeto|fazer projeto/i.test(prompt)) {
          window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent("bpm:project-bank-new-project", { detail: { prompt } }));
          }, 250);
        }
        setAiPrompt("");
        setIsAiPromptActive(false);
        return;
      }
      window.dispatchEvent(
        new CustomEvent("bpm:nash-chat-compose", {
          detail: { prompt, autoSend: true },
        }),
      );
      setAiPrompt("");
      setIsAiPromptActive(false);
    } finally {
      setAiPromptPending(false);
    }
  };


  const metricCards = [
    {
      id: "all",
      label: "Tarefas",
      value: formatNumber(taskCounts.total),
      icon: <FaTasks className="dashboard-metric-icon dashboard-metric-icon--tasks text-2xl text-blue-500" />,
      description: "Todas as tarefas em aberto",
    },
    {
      id: "onTime",
      label: "Prazo",
      value: formatNumber(taskCounts.onTime),
      icon: <FaCheckCircle className="dashboard-metric-icon dashboard-metric-icon--on-time text-2xl text-emerald-500" />,
      description: "Dentro do SLA definido",
    },
    {
      id: "alert",
      label: "Alerta",
      value: formatNumber(taskCounts.alert),
      icon: <FaExclamationTriangle className="dashboard-metric-icon dashboard-metric-icon--alert text-2xl text-amber-400" />,
      description: "Prazo próximo do vencimento",
    },
    {
      id: "late",
      label: "Atrasado",
      value: formatNumber(taskCounts.late),
      icon: <FaTimesCircle className="dashboard-metric-icon dashboard-metric-icon--late text-2xl text-red-500" />,
      description: "Prazo excedido",
    },
  ];

  const panelOptions = [
    {
      id: "overview",
      label: "Operacional",
      icon: <FaGlobeAmericas className="text-base" />,
    },
    {
      id: "insights",
      label: "Indicadores",
      icon: <FaChartLine className="text-base" />,
    },
  ];

  const hasFilterApplied = taskFilter !== "all";

  const topTodayMessages = useMemo(() => todayMessages.slice(0, 3), [todayMessages]);
  const extraTodayMessages = Math.max(todayMessages.length - topTodayMessages.length, 0);

  return (
    <div className="space-y-10 bg-white px-4 py-6 text-slate-800 sm:px-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          {isAiPromptActive ? (
            <form onSubmit={handleDashboardAiSubmit} className="max-w-2xl">
              <input
                autoFocus
                type="text"
                value={aiPrompt}
                onChange={(event) => setAiPrompt(event.target.value)}
                onBlur={() => {
                  if (!aiPrompt.trim()) {
                    setIsAiPromptActive(false);
                  }
                }}
                placeholder="Digite para a Nash IA..."
                disabled={aiPromptPending}
                className="w-full rounded-2xl border border-blue-200 bg-white px-4 py-3 text-2xl font-semibold text-slate-900 shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 disabled:opacity-70"
              />
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setIsAiPromptActive(true)}
              className="group rounded-2xl text-left transition hover:text-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100"
              title="Clique para escrever para a Nash IA"
            >
              <span className="block text-3xl font-semibold text-slate-900 group-hover:text-blue-700">
                {typedDashboardAiGreeting || "\u00a0"}
                <span className="ml-1 text-blue-600" aria-hidden="true">|{dashboardAiCursorFrame ? "_" : "/"}</span>
              </span>
            </button>
          )}
        </div>
        {hasFilterApplied && (
          <button
            type="button"
            onClick={handleClearTaskFilter}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
            title="Limpar filtros"
          >
            <span className="sr-only">Limpar filtros</span>
            <FaSyncAlt className="text-lg" />
          </button>
        )}
      </header>

      <div className="flex flex-wrap gap-2">
        {panelOptions.map(
          (option) => (
            <button
              key={option.id}
              onClick={() => handlePanelSelect(option.id)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                activePanel === option.id
                  ? "bg-slate-900 text-white shadow"
                  : "border border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <span className="flex items-center gap-2">
                {option.icon}
                <span>{option.label}</span>
              </span>
            </button>
          )
        )}
      </div>

      {activePanel === "overview" && (
        <>
          <section className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 xl:grid-cols-6">
            <div className="flex h-full flex-col rounded-3xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm lg:col-span-2 xl:col-span-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-emerald-500">
                    lembretes da agenda
                  </p>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Próximos lembretes
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => openTab("agenda", "Agenda", Agenda, { openTab })}
                  className="dashboard-agenda-open-button inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-white px-3 py-1 text-[11px] font-medium text-emerald-700 transition hover:bg-emerald-50"
                >
                  <FaCalendarAlt className="text-xs" />
                  Abrir
                </button>
              </div>
              <div className="mt-3 flex-1">
                {calendarLoading ? (
                  <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
                    Carregando lembretes...
                  </div>
                ) : calendarError ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-600">
                    {calendarError}
                  </div>
                ) : topTodayMessages.length ? (
                  <ul className="space-y-2 text-sm text-emerald-700">
                    {topTodayMessages.map((message, index) => (
                      <li
                        key={`${message}-${index}`}
                        className="flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/80 p-3"
                      >
                        <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                          <FaBell className="text-[10px]" />
                        </span>
                        <span>{message}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/70 p-3 text-xs text-emerald-700">
                    Nenhum lembrete para as próximas 48 horas.
                  </div>
                )}
              </div>
              {extraTodayMessages > 0 && (
                <p className="mt-2 text-[11px] font-medium text-emerald-600/80">
                  +{extraTodayMessages} lembretes adicionais disponíveis na agenda
                </p>
              )}
            </div>

            {metricCards.map((card, index) => {
              const isActive = taskFilter === card.id;
              const theme = getMetricCardTheme(card.id, index);
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setTaskFilter(card.id)}
                  aria-pressed={isActive}
                  title={card.description}
                  className={`dashboard-metric-card relative block w-full self-start overflow-hidden rounded-2xl bg-gradient-to-br ${theme.background} p-6 text-left ${theme.textColor} transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${theme.focusRing} lg:col-span-1 xl:col-span-1 ${
                    isActive
                      ? `ring-2 ${theme.activeRing} ring-offset-2 shadow-2xl`
                      : `ring-1 ${theme.inactiveRing} shadow-md hover:shadow-xl`
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm font-semibold uppercase tracking-wide ${theme.labelColor}`}>
                        {card.label}
                      </p>
                      <p className="mt-3 text-4xl font-bold">{card.value}</p>
                    </div>
                    <div className={`rounded-xl p-3 shadow-inner ${theme.iconWrapper}`}>
                      {card.icon}
                    </div>
                  </div>
                </button>
              );
            })}
          </section>

          <section className="dashboard-tasks-shell rounded-3xl border border-blue-100 bg-blue-50 p-6 shadow-sm">
            {tasksLoading ? (
              <div className="rounded-2xl border border-blue-200 bg-white/80 p-4 text-sm font-medium text-blue-700 shadow-inner">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span>Atualizando...</span>
                  <span>{tasksLoadProgress}%</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-blue-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 via-sky-400 to-cyan-300 transition-all duration-300"
                    style={{ width: `${tasksLoadProgress}%` }}
                  />
                </div>
              </div>
            ) : directHighlights.length || (hasSectorContext && sectorHighlights.length) ? (
              <div
                className={`grid gap-4 ${hasSectorContext ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}
              >
                <div className="dashboard-tasks-column flex flex-col gap-4 rounded-3xl border border-blue-100 bg-white p-4 shadow-sm">
                  <div className="space-y-2">
                    <div className="flex w-full flex-wrap items-center justify-between gap-2 rounded-2xl bg-[#071f54] px-4 py-3 text-white shadow-sm">
                      <h3 className="inline-flex items-center gap-2 text-lg font-semibold text-white">
                        <FaUser className="text-base" aria-hidden="true" />
                        Suas tarefas
                      </h3>
                      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/90">
                        {directAssignments.length
                          ? formatNumber(directAssignments.length)
                          : "0"}
                      </span>
                    </div>
                    {directAssignments.length === 0 && (
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-500">
                        {hasFilterApplied
                          ? "Nenhuma tarefa no filtro selecionado"
                          : "Nenhuma tarefa em aberto"}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    {directHighlights.length ? (
                      directHighlights.map((item) => renderAssignmentCard(item))
                    ) : (
                      <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/70 p-4 text-sm text-blue-600">
                        {hasFilterApplied
                          ? "Nenhuma tarefa no filtro selecionado."
                          : "Nenhuma tarefa."}
                      </div>
                    )}
                  </div>
                </div>
                {hasSectorContext && (
                  <div className="dashboard-tasks-column flex flex-col gap-4 rounded-3xl border border-blue-100 bg-white p-4 shadow-sm">
                    <div className="space-y-2">
                      <div className="flex w-full flex-wrap items-center justify-between gap-2 rounded-2xl bg-[#071f54] px-4 py-3 text-white shadow-sm">
                        <h3 className="inline-flex items-center gap-2 text-lg font-semibold text-white">
                          <FaUsers className="text-base" aria-hidden="true" />
                          Tarefas do setor
                        </h3>
                        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-white/90">
                          {sectorAssignments.length
                            ? formatNumber(sectorAssignments.length)
                            : "0"}
                        </span>
                      </div>
                      {sectorAssignments.length === 0 && (
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-500">
                          {hasFilterApplied
                            ? "Nenhuma tarefa no filtro selecionado para o setor"
                            : "Nenhuma tarefa em aberto no setor"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      {sectorHighlights.length ? (
                        sectorHighlights.map((item) => renderAssignmentCard(item))
                      ) : (
                        hasFilterApplied ? (
                          <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/70 p-4 text-sm text-blue-600">
                            Nenhuma tarefa no filtro selecionado para o seu setor.
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50 p-6 text-center text-blue-600">
                {hasFilterApplied
                  ? "Nenhuma tarefa no filtro selecionado. Ajuste o filtro para visualizar outras tarefas."
                  : "Nenhuma tarefa pendente. Aproveite para revisar indicadores e processos concluídos."}
              </div>
            )}
          </section>
        </>
      )}

      {activePanel === "insights" && (
        <div className="space-y-8">
          {advancedLoading && (
            <div
              className="rounded-3xl border border-blue-100 bg-blue-50 p-4 text-center text-sm text-blue-700 shadow-sm"
              aria-live="polite"
            >
              Carregando indicadores e histórico completo
              {completedHistoryCount > 0
                ? ` — ${formatNumber(completedHistoryCount)} conclusões verificadas`
                : "..."}
            </div>
          )}

          {advancedError && !advancedLoading && (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-600 shadow-sm">
              {advancedError}
            </div>
          )}

          {!advancedError && (
            <>
              {advancedWarnings.length > 0 && (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700 shadow-sm">
                  <ul className="list-disc space-y-1 pl-4">
                    {advancedWarnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <section className="overflow-hidden rounded-3xl border border-blue-100 bg-white shadow-sm">
                <header className="relative overflow-hidden bg-gradient-to-r from-[#071f54] via-[#0b3474] to-[#0d4d91] px-5 py-5 text-white sm:px-7 sm:py-6">
                  <div className="pointer-events-none absolute -right-12 -top-20 h-44 w-44 rounded-full bg-cyan-400/15 blur-3xl" />
                  <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.32em] text-cyan-300">
                        Central de indicadores
                      </p>
                      <h2 className="mt-2 text-2xl font-semibold sm:text-3xl">
                        Desempenho das tarefas do BPM
                      </h2>

                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span
                        className={`rounded-full border px-3 py-1.5 ${
                          completedHistoryComplete
                            ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-200"
                            : "border-white/10 bg-white/5 text-slate-300"
                        }`}
                      >
                        {advancedLoading
                          ? `Verificando ${formatNumber(completedHistoryCount)} conclusões`
                          : completedHistoryComplete
                            ? `Histórico completo de ${COMPLETED_ANALYTICS_DAYS} dias`
                            : "Histórico de conclusões indisponível"}
                      </span>
                      {advancedUpdatedAt && (
                        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-slate-300">
                          Atualizado às {advancedUpdatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </div>
                  </div>
                </header>

                <div className="p-5 sm:p-7">
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[0.8fr_1.4fr_1fr_auto]">
                    <label className="space-y-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
                      Período
                      <select
                        value={indicatorPeriodDays}
                        onChange={(event) => setIndicatorPeriodDays(Number(event.target.value))}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      >
                        <option value={30}>Últimos 30 dias</option>
                        <option value={90}>Últimos 90 dias</option>
                        <option value={180}>Últimos 180 dias</option>
                        <option value={365}>Últimos 365 dias</option>
                      </select>
                    </label>

                    <label className="space-y-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
                      Processo
                      <select
                        value={indicatorProcess}
                        onChange={(event) => setIndicatorProcess(event.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      >
                        {indicatorProcessOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="space-y-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
                      Situação nos gráficos
                      <select
                        value={indicatorStatus}
                        onChange={(event) => setIndicatorStatus(event.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="all">Abertas + concluídas</option>
                        <option value="open">Somente abertas</option>
                        <option value="completed">Somente concluídas</option>
                      </select>
                    </label>

                    <div className="flex items-end gap-2">
                      <button
                        type="button"
                        onClick={() => setPanelRefreshToken((current) => current + 1)}
                        disabled={advancedLoading}
                        className="inline-flex min-h-[42px] flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-60 xl:flex-none"
                      >
                        <FaSyncAlt className={advancedLoading ? "animate-spin" : ""} />
                        Atualizar
                      </button>
                      <button
                        type="button"
                        onClick={handleResetIndicatorFilters}
                        className="min-h-[42px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        Limpar
                      </button>
                    </div>
                  </div>



                  <div className="mt-5 grid grid-cols-2 gap-3 border-t border-slate-100 pt-5 sm:grid-cols-4">
                    {[
                      { label: "Processos", value: globalProcesses.length, icon: <FaCogs /> },
                      { label: "Formulários", value: globalForms.length, icon: <FaWpforms /> },
                      { label: "Acesso externo", value: formsWithExternal, icon: <FaGlobeAmericas /> },
                      { label: "Ciclo médio", value: avgCompletionAll ? formatDuration(avgCompletionAll) : "-", icon: <FaStopwatch /> },
                    ].map((item) => (
                      <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
                          <span className="text-blue-600">{item.icon}</span>
                          {item.label}
                        </div>
                        <p className="mt-2 text-xl font-semibold text-slate-900">{formatNumber(item.value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {advancedCards.map((card) => (
                  <div
                    key={card.id}
                    className="group relative min-h-[145px] overflow-hidden rounded-2xl border border-blue-100 bg-white p-5 text-slate-800 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md"
                  >
                    <div className="absolute -right-8 -top-10 h-28 w-28 rounded-full bg-white/10 transition group-hover:scale-125" />
                    <div className="relative flex h-full items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
                          {card.label}
                        </p>
                        <p className="mt-3 text-4xl font-semibold tracking-tight text-slate-900">{formatNumber(card.value)}</p>
                        <p className="mt-2 text-xs leading-relaxed text-slate-500">{card.description}</p>
                      </div>
                      <div className="shrink-0 rounded-xl bg-blue-50 p-3 text-blue-700">
                        {card.icon}
                      </div>
                    </div>
                  </div>
                ))}
              </section>

              <section className="grid gap-6 lg:grid-cols-2 xl:grid-cols-12">
                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6 lg:col-span-1 xl:col-span-7">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                        visão histórica
                      </p>
                      <h2 className="text-xl font-semibold text-slate-900">{indicatorStatusLabel} por mês</h2>
                    </div>
                    <span className="self-start rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600 sm:self-auto">
                      {formatNumber(monthlyTrend.total)} movimentos em 6 meses
                    </span>
                  </div>

                  <div className="mt-6">
                    {monthlyTrend.points.length ? (
                      <div>
                        <svg
                          viewBox={`0 0 ${monthlyTrend.chart.width} ${monthlyTrend.chart.height}`}
                          className="h-auto w-full"
                          preserveAspectRatio="xMidYMid meet"
                        >
                          <defs>
                            <linearGradient id="monthlyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(139, 92, 246, 0.38)" />
                              <stop offset="55%" stopColor="rgba(59, 130, 246, 0.2)" />
                              <stop offset="100%" stopColor="rgba(34, 211, 238, 0.04)" />
                            </linearGradient>
                          </defs>
                          {monthlyTrend.areaPath && (
                            <path d={monthlyTrend.areaPath} fill="url(#monthlyGradient)" stroke="none" />
                          )}
                          <polyline
                            points={monthlyTrend.points.map((point) => `${point.x},${point.y}`).join(" ")}
                            fill="none"
                            stroke="#6366f1"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          {monthlyTrend.points.map((point, index) => (
                            <g key={`${point.x}-${index}`}>
                              <circle
                                cx={point.x}
                                cy={point.y}
                                r={6}
                                fill="#8b5cf6"
                                stroke="#e0e7ff"
                                strokeWidth="2"
                              />
                              <text
                                x={point.x}
                                y={point.y - 14}
                                textAnchor="middle"
                                className="fill-blue-700 text-[10px] font-semibold"
                              >
                                {point.value}
                              </text>
                            </g>
                          ))}
                        </svg>
                        <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wide text-blue-600 sm:flex sm:items-center sm:justify-between sm:text-xs">
                          {monthlyTrend.labels.map((label, index) => (
                            <span key={`${label}-${index}`}>{label}</span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex h-48 items-center justify-center rounded-2xl border border-dashed border-blue-200 bg-blue-50 text-blue-600">
                        Aguarde movimentações para visualizar a tendência mensal.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6 lg:col-span-1 xl:col-span-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                        janela dinâmica
                      </p>
                      <h2 className="text-xl font-semibold text-slate-900">Últimos movimentos</h2>
                      <p className="text-sm text-slate-500">
                        Ajuste a quantidade de dias para observar picos de entrada.
                      </p>
                    </div>
                    <div className="flex items-center gap-2" role="group" aria-label="Janela dos últimos movimentos">
                      <FaStopwatch className="text-violet-500" />
                      {[7, 14, 30].map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => setRecentDays(days)}
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                            recentDays === days
                              ? "bg-violet-600 text-white shadow"
                              : "bg-slate-100 text-slate-600 hover:bg-violet-50 hover:text-violet-700"
                          }`}
                        >
                          {days}d
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6">
                    {periodTrend.buckets.length ? (
                      <div className="overflow-x-auto pb-3">
                        <div className="flex min-w-max items-end gap-2 sm:gap-3">
                          {periodTrend.buckets.map((bucket, index) => {
                            const height = periodTrend.max
                              ? Math.max((bucket.count / periodTrend.max) * 120, 4)
                              : 4;
                            return (
                              <div
                                key={bucket.label}
                                className="w-11 shrink-0 text-center sm:w-12"
                              >
                                <div className="mx-auto flex h-32 w-9 items-end justify-center rounded-full bg-slate-100">
                                  <div
                                    className={`w-3/4 rounded-full bg-gradient-to-t ${
                                      [
                                        "from-violet-600 to-fuchsia-400",
                                        "from-blue-600 to-cyan-400",
                                        "from-emerald-600 to-lime-400",
                                        "from-amber-500 to-orange-400",
                                      ][index % 4]
                                    }`}
                                    style={{ height: `${height}px` }}
                                    title={`${bucket.count} solicitações`}
                                  />
                                </div>
                                <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-slate-600">
                                  {bucket.label}
                                </p>
                                <p className="text-xs text-slate-500">{formatNumber(bucket.count)}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="flex h-48 items-center justify-center rounded-2xl border border-dashed border-blue-200 bg-blue-50 text-blue-600">
                        Nenhuma nova solicitação no período selecionado.
                      </div>
                    )}
                  </div>

                  <p className="mt-4 text-xs uppercase tracking-[0.25em] text-slate-400">
                    {formatNumber(periodTrend.total)} solicitações nos últimos {periodTrend.windowDays} dias
                  </p>
                </div>
              </section>

              <section className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6">
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    setores do bpm
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">Processos por categoria</h2>
                  <div className="mt-6 space-y-4">
                    {processCategoryDistribution.length ? (
                      processCategoryDistribution.map((item, index) => (
                        <div key={item.name}>
                          <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                            <span>{item.name}</span>
                            <span>{formatNumber(item.value)}</span>
                          </div>
                          <div className="mt-2 h-2 w-full rounded-full bg-blue-100">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${
                                [
                                  "from-violet-500 to-fuchsia-400",
                                  "from-cyan-500 to-blue-500",
                                  "from-emerald-500 to-lime-400",
                                  "from-amber-500 to-orange-400",
                                ][index % 4]
                              }`}
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                          <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                            {item.percentage}% do portfólio monitorado
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">
                        Os processos serão exibidos conforme as permissões de acesso disponíveis.
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6">
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    fluxos em destaque
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">Processos mais movimentados</h2>
                  <div className="mt-6 space-y-4">
                    {topProcessesByVolume.length ? (
                      topProcessesByVolume.map((item, index) => (
                        <div key={item.name}>
                          <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                            <span>{item.name}</span>
                            <span>{formatNumber(item.value)}</span>
                          </div>
                          <div className="mt-2 h-2 w-full rounded-full bg-blue-100">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${
                                [
                                  "from-indigo-500 to-violet-500",
                                  "from-blue-500 to-cyan-400",
                                  "from-teal-500 to-emerald-400",
                                  "from-orange-500 to-amber-400",
                                  "from-pink-500 to-rose-400",
                                  "from-slate-600 to-slate-400",
                                ][index % 6]
                              }`}
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                          <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                            {item.percentage}% das solicitações monitoradas
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">
                        Assim que novas solicitações forem registradas elas aparecerão aqui.
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6">
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    saúde operacional
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">Status das filas atuais</h2>
                  <div className="mt-6 flex items-center gap-5 rounded-2xl bg-slate-50 p-4">
                    <div
                      className="relative h-28 w-28 shrink-0 rounded-full shadow-inner"
                      style={{ background: statusDonutBackground }}
                      role="img"
                      aria-label={`${filteredOpenItems.length} tarefas em aberto`}
                    >
                      <div className="absolute inset-4 flex flex-col items-center justify-center rounded-full bg-white shadow-sm">
                        <span className="text-2xl font-semibold text-slate-900">
                          {formatNumber(filteredOpenItems.length)}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">abertas</span>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">Saúde da fila</p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-500">
                        Distribuição das tarefas atuais entre prazo regular, atraso e atividades sem prazo.
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 space-y-4">
                    {globalStatusBreakdown.map((item) => (
                      <div key={item.label}>
                        <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                          <span>{item.label}</span>
                          <span>{formatNumber(item.value)}</span>
                        </div>
                        <div className="mt-2 h-2 w-full rounded-full bg-slate-100">
                          <div
                            className={`h-full rounded-full ${item.color}`}
                            style={{ width: `${Math.min(item.percentage || 0, 100)}%` }}
                          />
                        </div>
                        <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                          {item.percentage || 0}% das solicitações abertas
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

            </>
          )}
        </div>
      )}


    </div>
  );
}
